import React, { useState } from 'react';
import {
    FpButton, Field, TextInput, NumberInput, DateTimeInput, Checkbox, RadioRow,
} from '../../../components/frankopop/frankopopShared';
import { toLocalInput } from '../../../data/frankopop';

// Shared create/edit form for a FrankoPop set's configuration.
// `initial` is a set view (edit) or {} (create). Calls onSubmit(payload).
export default function SetConfigForm({ initial = {}, onSubmit, submitLabel = 'Save', onCancel, disableTypeToggle = false }) {
    const [name, setName] = useState(initial.name || '');
    const [setArt, setSetArt] = useState(initial.setArt || '');
    const [boxArt, setBoxArt] = useState(initial.boxArt || '');
    const [backArt, setBackArt] = useState(initial.backArt || '');
    const [giftableOnly, setGiftableOnly] = useState(!!initial.giftableOnly);
    const [giftPermission, setGiftPermission] = useState(initial.giftPermission || 'anyone_staff');
    const [priceCards, setPriceCards] = useState(initial.priceCards ?? 3);
    const [releaseAt, setReleaseAt] = useState(toLocalInput(initial.releaseAt));
    const [endAt, setEndAt] = useState(toLocalInput(initial.endAt));
    const [limitedRun, setLimitedRun] = useState(!!initial.limitedRun);
    const [limitedRunTotal, setLimitedRunTotal] = useState(initial.limitedRunTotal ?? 100);

    const shopDisabled = giftableOnly;

    const submit = () => {
        onSubmit({
            name,
            setArt: setArt.trim() || null,
            boxArt: boxArt.trim() || null,
            backArt: backArt.trim() || null,
            giftableOnly,
            giftPermission,
            priceCards,
            releaseAt: releaseAt || null,
            endAt: endAt || null,
            limitedRun,
            limitedRunTotal,
        });
    };

    return (
        <div className="space-y-4">
            <Field label="Set name" required>
                <TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Winterfest Wonderland" />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Field label="Set artwork" hint="Paste an image URL - blank shows a placeholder for now. Also the FrankoPop's packaging when boxed.">
                    <TextInput value={setArt} onChange={(e) => setSetArt(e.target.value)} placeholder="/img/… or https://…" />
                </Field>
                <Field label="Box artwork" hint="The blind-box art shown in the shop.">
                    <TextInput value={boxArt} onChange={(e) => setBoxArt(e.target.value)} placeholder="/img/… or https://…" />
                </Field>
                <Field label="Back artwork" hint="Renders behind the FrankoPop when boxed. Blank uses the default.">
                    <TextInput value={backArt} onChange={(e) => setBackArt(e.target.value)} placeholder="/img/… or https://…" />
                </Field>
            </div>

            <div className="border-2 border-gray-200 p-3">
                <Checkbox
                    label="Giftable only (not sold in the shop)"
                    checked={giftableOnly}
                    disabled={disableTypeToggle}
                    onChange={setGiftableOnly}
                />
                {disableTypeToggle && (
                    <p className="mt-1 text-[11px] text-gray-400">Set type is locked - boxes from this set already exist.</p>
                )}

                {giftableOnly ? (
                    <div className="mt-3">
                        <Field label="Who can gift boxes from this set?">
                            <RadioRow
                                value={giftPermission}
                                onChange={setGiftPermission}
                                options={[
                                    { value: 'anyone_staff', label: 'Anyone on staff' },
                                    { value: 'admins_only', label: 'Admins only' },
                                ]}
                            />
                        </Field>
                        <p className="mt-2 text-[11px] text-gray-500">
                            Anyone on staff → any staff member may gift up to 5 boxes from this set per week.
                            Admins → only admins (no limit).
                        </p>
                    </div>
                ) : (
                    <p className="mt-2 text-[11px] text-gray-500">Shop set - configure its sale window, price and stock below.</p>
                )}
            </div>

            <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${shopDisabled ? 'opacity-50' : ''}`}>
                <Field label="Release / start date" disabled={shopDisabled} hint="Can be in the future.">
                    <DateTimeInput value={releaseAt} disabled={shopDisabled} onChange={(e) => setReleaseAt(e.target.value)} />
                </Field>
                <Field label="End date (optional)" disabled={shopDisabled} hint="Blank = available indefinitely (unless it sells out).">
                    <DateTimeInput value={endAt} disabled={shopDisabled} onChange={(e) => setEndAt(e.target.value)} />
                </Field>
                <Field label="Price (framp cards)" disabled={shopDisabled} required={!shopDisabled} hint="10 framps = 1 framp card.">
                    <NumberInput min={1} value={priceCards} disabled={shopDisabled} onChange={(e) => setPriceCards(e.target.value)} />
                </Field>
                <div>
                    <Checkbox label="Limited run" checked={limitedRun} disabled={shopDisabled} onChange={setLimitedRun} />
                    {limitedRun && !shopDisabled && (
                        <div className="mt-2">
                            <NumberInput min={1} value={limitedRunTotal} onChange={(e) => setLimitedRunTotal(e.target.value)} />
                            <p className="mt-1 text-[11px] text-gray-400">Total boxes that can ever be sold through the shop.</p>
                        </div>
                    )}
                </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end pt-2">
                {onCancel && <FpButton variant="secondary" onClick={onCancel}>Cancel</FpButton>}
                <FpButton variant="primary" onClick={submit} disabled={!name.trim()}>{submitLabel}</FpButton>
            </div>
        </div>
    );
}
